import type React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";

import { useImageResolver } from "@/api/hooks/useImageResolver";
import type {
  CommunityFeedAudienceGroupV2Dto,
  CommunityFeedAudienceProjectConnectionV2Dto,
  CommunityFeedAudienceProjectV2Dto,
  CommunityFeedAudienceV2Dto,
} from "@/api/types";
import { AudienceSelectorButton } from "@/components/AudienceSelector/AudienceSelectorButton";
import { Drawer } from "@/components/Drawer/Drawer";
import { FormErrorWrapper } from "@/components/Form/FormErrorWrapper";
import { FormRadioCardGroup } from "@/components/Form/FormRadioCard";
import { GroupIcon } from "@/components/GroupIcon/GroupIcon";
import { Asterisk01Icon } from "@/components/Icons/Asterisk01Icon";
import { Building01Icon } from "@/components/Icons/Building01Icon";
import { Building04Icon } from "@/components/Icons/Building04Icon";
import { Building07Icon } from "@/components/Icons/Building07Icon";
import { LastActivityLabel } from "@/components/Label/LastActivityLabel";
import type { PillListItem } from "@/components/Pill/PillList";
import { RadioCard } from "@/components/Radio/RadioCard";
import { SearchInput } from "@/components/SearchInput/SearchInput";
import { Button } from "@/design-system/Button";
import { Column, Columns } from "@/design-system/Columns";
import { ContentBlock } from "@/design-system/ContentBlock";
import { SearchableSelect } from "@/design-system/SearchableSelect";
import { Stack } from "@/design-system/Stack";
import { Text } from "@/design-system/Text";
import type { AudienceFormValues } from "@/helpers/community-feed";
import { isDefined } from "@/helpers/util";
import { useProject } from "@/hooks/Network/useProject";
import { useBool } from "@/hooks/useBool";
import { useTranslation } from "@/translations";

type AdminCreatePostFormAudienceSelectorProps = {
  audience: CommunityFeedAudienceV2Dto;
  selectedGroup: CommunityFeedAudienceGroupV2Dto | undefined;
  selectedProject: CommunityFeedAudienceProjectV2Dto | undefined;
  selectedProjectConnection: CommunityFeedAudienceProjectConnectionV2Dto | undefined;
  onSelectGroup: (group: CommunityFeedAudienceGroupV2Dto) => void;
  onSelectProject: (project: CommunityFeedAudienceProjectV2Dto) => void;
  onSelectProjectConnection: (projectConnection: CommunityFeedAudienceProjectConnectionV2Dto) => void;
  isDisabled?: boolean;
};
export function AdminCreatePostFormAudienceSelector({
  audience,
  selectedGroup,
  selectedProject,
  selectedProjectConnection,
  onSelectGroup,
  onSelectProject,
  onSelectProjectConnection,
  isDisabled,
}: AdminCreatePostFormAudienceSelectorProps): React.ReactNode {
  const project = useProject();
  const audienceProjectConnections = audience.projectConnections;
  const audienceProject = audience.project;
  // No posting to project connection groups
  const realEstateGroups = useMemo(
    () => audience.realEstateGroups.filter((x) => x.selectableAudience.some((a) => a.project)),
    [audience],
  );
  const interestGroups = useMemo(
    () => audience.interestGroups.filter((x) => x.selectableAudience.some((a) => a.project)),
    [audience],
  );

  // Interal state to handle temporary selections
  const [selection, setSelection] = useState({
    group: selectedGroup,
    project: selectedProject,
    projectConnection: selectedProjectConnection,
  });
  const [isDrawerOpened, drawerHandler] = useBool();
  const [realEstateGroupSearch, setRealEstateGroupSearch] = useState("");
  const { t } = useTranslation();
  const form = useFormContext<AudienceFormValues>();

  useEffect(() => {
    form.register("audienceType", {
      validate: (value) => {
        if (!value) {
          return t("components.form.error.required", {
            inputName: t("common.entity.audience"),
          });
        }
      },
    });
  }, [form, t]);

  const reset = useCallback(() => {
    setRealEstateGroupSearch("");
    setSelection({
      group: selectedGroup,
      project: selectedProject,
      projectConnection: selectedProjectConnection,
    });
  }, [selectedGroup, selectedProject, selectedProjectConnection]);

  // Update internal state when prop changes (e.g. Parent form reset)
  useEffect(() => {
    // oxlint-disable-next-line react-hooks/set-state-in-effect
    reset();
  }, [reset]);

  const handleSelectGroup = (currGroup: CommunityFeedAudienceGroupV2Dto | undefined) => {
    setSelection({
      group: currGroup,
      project: undefined,
      projectConnection: undefined,
    });
  };
  const handleSelectProject = (currProject: CommunityFeedAudienceProjectV2Dto | undefined) => {
    setSelection({
      group: undefined,
      project: currProject,
      projectConnection: undefined,
    });
  };

  const handleSelectProjectConnection = (currProject: CommunityFeedAudienceProjectConnectionV2Dto | undefined) => {
    setSelection({
      group: undefined,
      project: undefined,
      projectConnection: currProject,
    });
  };

  const handleConfirmSelection = () => {
    if (selection.group) {
      onSelectGroup(selection.group);
    } else if (selection.projectConnection) {
      onSelectProjectConnection(selection.projectConnection);
    } else if (selection.project) {
      onSelectProject(selection.project);
    }
    reset();
  };

  const getAudienceIcon = (type: "project" | "realEstateGroup" | "interestGroup" | "projectConnection") => {
    switch (type) {
      case "realEstateGroup":
        return <Building01Icon size={24} />;
      case "interestGroup":
        return <Asterisk01Icon size={24} />;
      case "projectConnection":
        return <Building07Icon size={24} />;
      case "project":
      default:
        return <Building04Icon size={24} />;
    }
  };

  const filteredRealEstateGroups = realEstateGroups.filter((group) =>
    group.name.toLowerCase().includes(realEstateGroupSearch.toLowerCase()),
  );

  let projectDescription: string | undefined;
  if (project.type === "companyBased") {
    projectDescription = t("page.admin-create-post.form.audience.group.members", {
      count: audienceProject?.audienceCount ?? 0,
    });
  } else if (isDefined(audienceProject?.totalAddressCount)) {
    projectDescription = t("page.admin-create-post.form.audience.group.addresses", {
      count: `${audienceProject.addressWithUsersCount} / ${audienceProject.totalAddressCount}`,
    });
  }

  const selectedAudienceItems = useMemo(() => {
    const items: PillListItem[] = [];

    if (!selectedGroup && !selectedProject && !selectedProjectConnection) {
      items.push({
        label: "[...]",
      });
    } else if (selectedGroup) {
      items.push({
        icon: selectedGroup.type === "realEstate" ? <Building01Icon /> : <Asterisk01Icon />,
        label: selectedGroup.name,
      });
    } else if (selectedProject) {
      items.push({
        icon: <Building04Icon />,
        label:
          project.type === "companyBased"
            ? t("page.admin-create-post.form.audience.all-members", {
                name: selectedProject.name,
              })
            : t("page.admin-create-post.form.audience.all-residents", {
                name: selectedProject.name,
              }),
      });
    } else if (selectedProjectConnection) {
      items.push({
        icon: <Building07Icon />,
        label: selectedProjectConnection.name,
      });
    }

    return items;
  }, [project.type, selectedGroup, selectedProject, selectedProjectConnection, t]);

  return (
    <>
      <FormErrorWrapper<AudienceFormValues> name="audienceType">
        <AudienceSelectorButton
          items={selectedAudienceItems}
          isDisabled={isDisabled}
          isError={form.formState.errors.audienceType != null}
          onClick={drawerHandler.setTrue}
          data-testid="audience-selector-btn"
        />
      </FormErrorWrapper>

      <Drawer.Root
        data-testid="audience-selector-drawer"
        title={t("page.admin-create-post.audience-selector.drawer.title")}
        isOpened={isDrawerOpened}
        onOpenChange={(state) => {
          if (!state) {
            reset();
          }
          drawerHandler.set(state);
        }}
      >
        <Drawer.Body>
          <Stack gap="lg">
            {/* Project & Real estate groups */}
            {(audienceProject || filteredRealEstateGroups.length > 0) && (
              <Stack gap="md">
                <h4 className="text-heading-4">{t("page.admin-create-post.audience-selector.drawer.project.label")}</h4>
                {realEstateGroups.length > 5 && (
                  <SearchInput
                    placeholder={t("page.admin-create-post.audience-selector.drawer.real-estate-groups.placeholder")}
                    onChange={(e) => setRealEstateGroupSearch(e.target.value)}
                  />
                )}
                <FormRadioCardGroup layout="vertical">
                  {audienceProject && (
                    <RadioCard
                      data-testid="audience-project-btn"
                      name="audience"
                      label={
                        project.type === "companyBased"
                          ? t("page.admin-create-post.form.audience.all-members", {
                              name: audienceProject.name,
                            })
                          : t("page.admin-create-post.form.audience.all-residents", {
                              name: audienceProject.name,
                            })
                      }
                      description={projectDescription}
                      icon={getAudienceIcon("project")}
                      checked={Boolean(selection.project)}
                      value="project"
                      onChange={() => handleSelectProject(audienceProject)}
                    />
                  )}
                  {filteredRealEstateGroups.length === 0 ? (
                    <span className="text-caption">{t("common.label.no-result")}</span>
                  ) : (
                    filteredRealEstateGroups.map((realEstateGroup) => {
                      const icon = getAudienceIcon("realEstateGroup");
                      const isSelected = selection.group?.id === realEstateGroup.id;
                      const projectGroupAudience = realEstateGroup.selectableAudience.find((a) => a.type === "project");
                      let description: string | undefined;
                      if (project.type === "addressBased") {
                        description = projectGroupAudience
                          ? t("page.admin-create-post.form.audience.group.addresses", {
                              count: projectGroupAudience?.project?.addressCount ?? 0,
                            })
                          : undefined;
                      } else {
                        description = projectGroupAudience
                          ? t("page.admin-create-post.form.audience.group.members", {
                              count: projectGroupAudience?.project?.audienceCount ?? 0,
                            })
                          : undefined;
                      }

                      return (
                        <RadioCard
                          name="audience"
                          data-testid="audience-real-estate-group-btn"
                          key={realEstateGroup.id}
                          label={realEstateGroup.name}
                          description={description}
                          icon={icon}
                          checked={isSelected}
                          value={realEstateGroup.id}
                          onChange={() => handleSelectGroup(realEstateGroup)}
                        />
                      );
                    })
                  )}
                </FormRadioCardGroup>
              </Stack>
            )}
            {/* Project connections */}
            {audienceProjectConnections.length > 0 && (
              <Stack gap="md">
                <h4 className="text-heading-4">
                  {t("page.admin-create-post.audience-selector.drawer.project-connections.label")}
                </h4>
                <FormRadioCardGroup layout="vertical">
                  {audienceProjectConnections.map((projectConnection) => {
                    const icon = getAudienceIcon("projectConnection");
                    const isSelected = selection.projectConnection?.id === projectConnection.id;
                    const description =
                      project.type === "companyBased"
                        ? t("page.admin-create-post.form.audience.project-connection.members", {
                            count: projectConnection.audienceCount,
                          })
                        : t("page.admin-create-post.form.audience.project-connection.audience", {
                            count: projectConnection.audienceCount,
                          });

                    return (
                      <RadioCard
                        key={projectConnection.id}
                        name="audience"
                        data-testid="audience-project-connection-btn"
                        value={projectConnection.id}
                        label={projectConnection.name}
                        description={description}
                        icon={icon}
                        checked={isSelected}
                        onChange={() => handleSelectProjectConnection(projectConnection)}
                      />
                    );
                  })}
                </FormRadioCardGroup>
              </Stack>
            )}
            {/* Interest groups */}
            {interestGroups.length > 0 && (
              <Stack gap="md">
                <h4 className="text-heading-4">
                  {audienceProject || realEstateGroups.length > 0 || audienceProjectConnections.length > 0
                    ? t("page.admin-create-post.audience-selector.drawer.or-interest-groups.label")
                    : t("page.admin-create-post.audience-selector.drawer.interest-groups.label")}
                </h4>
                <RadioCard
                  name="audience"
                  icon={getAudienceIcon("interestGroup")}
                  label={t("page.admin-create-post.audience-selector.drawer.interest-groups.option.label")}
                  value="interestGroup"
                  checked={interestGroups.some((group) => group.id === selection.group?.id)}
                  onChange={() => {}}
                >
                  <div className="w-full" onClick={(e) => e.preventDefault()}>
                    <SearchableSelect
                      keySelector={(group) => group.id}
                      renderOption={(group) => <AdminCreatePostAudienceSelectorGroupOption group={group} />}
                      renderSelected={(group) => group.name}
                      searchableFieldSelector={(group) => [group.name]}
                      selected={interestGroups.find((group) => group.id === selection.group?.id)}
                      placeholder={t("page.admin-create-post.audience-selector.drawer.interest-groups.placeholder")}
                      items={interestGroups}
                      onChange={handleSelectGroup}
                    />
                  </div>
                </RadioCard>
              </Stack>
            )}
          </Stack>
        </Drawer.Body>

        <Drawer.Actions>
          <Drawer.Close>
            <Button variant="subtle" tone="primary">
              {t("common.action.cancel")}
            </Button>
          </Drawer.Close>
          <Drawer.Close>
            <Button data-testid="confirm-audience-btn" variant="filled" onClick={handleConfirmSelection}>
              {t("common.action.select")}
            </Button>
          </Drawer.Close>
        </Drawer.Actions>
      </Drawer.Root>
    </>
  );
}

type AdminCreatePostAudienceSelectorGroupOptionProps = {
  group: CommunityFeedAudienceGroupV2Dto;
};

function AdminCreatePostAudienceSelectorGroupOption({ group }: AdminCreatePostAudienceSelectorGroupOptionProps) {
  const resolveImage = useImageResolver();
  const { t } = useTranslation();
  const audienceCount =
    group.selectableAudience.length === 1
      ? t("page.admin-create-post.form.audience.group.members", {
          count:
            group.selectableAudience[0].type === "project"
              ? (group.selectableAudience[0].project?.audienceCount ?? 0)
              : (group.selectableAudience[0].projectConnection?.audienceCount ?? 0),
        })
      : undefined;

  return (
    <ContentBlock>
      <Columns gap="sm" align="center">
        <Column width="content">
          {group.isResidentGroup && group.image ? (
            <img src={resolveImage(group.image, "sm")} alt={group.name} className="size-icon-control rounded-full" />
          ) : (
            <GroupIcon icon={group.icon} size={20} />
          )}
        </Column>
        <Column>
          <Stack>
            <Columns gap="sm" align="center">
              <Column>
                <Text overflow="truncate">{group.name}</Text>
              </Column>
              {audienceCount ? (
                <Column width="content">
                  <Text variant="caption" tone="secondary" overflow="nowrap">
                    {audienceCount}
                  </Text>
                </Column>
              ) : null}
            </Columns>
            <LastActivityLabel lastActivityAt={group.lastActivityAt} showNoActivity />
          </Stack>
        </Column>
      </Columns>
    </ContentBlock>
  );
}
